﻿import { FC } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, CardFooter, Button, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { ROUTES } from '../config/routes';
import { trackConsultationClick } from '../analytics/events';
import { PageProps } from './HomePage';

export const BusinessSolutionsPage: FC<PageProps> = ({ onNavigate }) => {
  const handleConsultation = (serviceKey: string) => {
    trackConsultationClick(`business_${serviceKey}`, 'business_solutions_page');
    onNavigate(ROUTES.CONTACT);
  };

  const businessServices = [
    {
      id: 'recruitment',
      title: 'Recruitment & Talent Acquisition',
      badge: 'Core HR',
      description: 'End-to-end recruitment pipelines tailored for fast-moving startups and small-to-medium enterprises requiring vetted, high-retention talent.',
      inclusions: [
        'Role specification, job description drafting & market compensation benchmarking',
        'Multi-channel candidate sourcing across targeted professional networks',
        'Structured competency-based pre-screening & candidate evaluation scorecards',
        'Interview scheduling coordination & continuous candidate communication',
        'Final stage reference checks and structured offer facilitation'
      ]
    },
    {
      id: 'hr-support',
      title: 'HR Operations & Support',
      badge: 'Operations',
      description: 'Foundational human resource infrastructure to maintain organizational alignment, statutory clarity, and professional employee experiences.',
      inclusions: [
        'Employee handbook & workplace policy framework documentation',
        'Seamless new-hire onboarding checklists & orientation structure',
        'Leave tracking, attendance policy alignment & internal workflow setup',
        'Performance evaluation review templates and objective metric tracking',
        'Day-to-day internal HR grievance & employee query management'
      ]
    },
    {
      id: 'virtual-assistance',
      title: 'Virtual Assistance',
      badge: 'Executive Support',
      description: 'Dependable, organized remote administrative assistance allowing founders and executive teams to delegate operational overhead and focus on core growth.',
      inclusions: [
        'Executive calendar management, meeting scheduling & timezone coordination',
        'Inbox triage, critical email prioritization & professional correspondence',
        'Document formatting, presentation deck preparation & proofreading',
        'Desk research, industry data compilation & competitive analysis summaries',
        'Routine operational coordination and project follow-up tracking'
      ]
    },
    {
      id: 'lead-generation',
      title: 'Lead Generation',
      badge: 'B2B Growth',
      description: 'Systematic business-to-business pipeline building focused on quality prospect identification rather than low-converting mass cold spam.',
      inclusions: [
        'Ideal Customer Profile (ICP) definition and target account mapping',
        'Curated prospect database building with verified professional contact data',
        'Customized value-driven email outreach cadence templates',
        'CRM data entry, lead categorization & follow-up tracking',
        'Weekly campaign activity & response engagement reporting'
      ]
    },
    {
      id: 'linkedin-outreach',
      title: 'LinkedIn Outreach',
      badge: 'B2B Networking',
      description: 'Organic, relationship-first executive presence and outbound connection management on LinkedIn designed to initiate credible B2B dialogues.',
      inclusions: [
        'Executive profile optimization tailored for commercial credibility',
        'Targeted stakeholder research within prospective client companies',
        'Thoughtful, personalized connection requests that respect professional etiquette',
        'Value-oriented follow-up messaging cadences without aggressive sales pitches',
        'Meeting handover coordination directly to your sales or leadership team'
      ]
    },
    {
      id: 'digital-marketing',
      title: 'Digital Marketing Support',
      badge: 'Brand Presence',
      description: 'Practical marketing execution that keeps your company brand active, consistent, and professionally positioned across key touchpoints.',
      inclusions: [
        'Professional company page content planning & monthly editorial scheduling',
        'Graphic asset coordination and brand consistency alignment',
        'Case study and service one-pager layout design support',
        'Audience engagement monitoring and inquiry redirection',
        'Digital channel metric summaries and engagement reviews'
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="B2B Business Solutions & HR Support"
        description="Comprehensive HR operations, recruitment, virtual assistance, lead generation, and digital marketing support for startups, entrepreneurs, and growing SMEs."
      />

      {/* Hero */}
      <section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid var(--color-border-subtle)', padding: '4.5rem 0' }}>
        <Container size="xl">
          <div style={{ maxWidth: '820px', margin: '0 auto', textAlign: 'center' }}>
            <Badge variant="teal" style={{ marginBottom: '1rem' }}>B2B & Startup Services</Badge>
            <h1 style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3rem)', fontWeight: 800, color: 'var(--color-navy-dark)', lineHeight: 1.2 }}>
              Practical HR & Business Support for Growing Companies
            </h1>
            <p style={{ fontSize: '1.15rem', color: 'var(--color-text-secondary)', marginTop: '1.25rem', lineHeight: 1.65 }}>
              Agile, reliable organizational support designed for startups, entrepreneurs, and SMEs seeking focused recruitment, structured HR operations, and executive leverage.
            </p>
            <div style={{ marginTop: '2rem' }}>
              <Button
                variant="primary"
                size="lg"
                onClick={() => handleConsultation('hero_cta')}
              >
                Book a Consultation
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Services Grid */}
      <Section title="Enterprise & Operational Capabilities" subtitle="Selectable standalone services or integrated operational packages tailored to your company's stage.">
        <div className="cs-grid-2">
          {businessServices.map(svc => (
            <Card key={svc.id} className="cs-card-hover" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <Badge variant="teal">{svc.badge}</Badge>
              </div>

              <CardTitle style={{ fontSize: '1.3rem' }}>{svc.title}</CardTitle>
              <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6, fontSize: '0.925rem' }}>
                {svc.description}
              </CardDescription>

              <div style={{ marginTop: '1.5rem', flex: '1 0 auto' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.65rem' }}>
                  Scope of Deliverables:
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.55rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                  {svc.inclusions.map((inc, idx) => (
                    <li key={idx} style={{ display: 'flex', gap: '0.5rem', lineHeight: 1.45 }}>
                      <span style={{ color: 'var(--color-brand-primary)', fontWeight: 700 }}>•</span>
                      <span>{inc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <CardFooter style={{ marginTop: '1.75rem', paddingTop: '1.25rem' }}>
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => handleConsultation(svc.id)}
                  style={{ width: '100%' }}
                >
                  Inquire About {svc.title}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </Section>

      {/* Why Outsource to Connect Source */}
      <Section
        title="Why Partner With Connect Source"
        subtitle="Flexible, pragmatic operational assistance without the overhead of immediate full-time internal hires."
        style={{ backgroundColor: '#ffffff', borderTop: '1px solid var(--color-border-subtle)', borderBottom: '1px solid var(--color-border-subtle)' }}
      >
        <div className="cs-grid-3">
          <Card style={{ padding: '1.75rem' }}>
            <CardTitle style={{ fontSize: '1.15rem' }}>Speed & Flexibility</CardTitle>
            <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>
              Scale support up or down based on hiring spikes, operational restructuring, or outbound campaigns without cumbersome long-term retainers.
            </CardDescription>
          </Card>
          <Card style={{ padding: '1.75rem' }}>
            <CardTitle style={{ fontSize: '1.15rem' }}>CHRM-Certified Standards</CardTitle>
            <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>
              Our HR workflows and documentation adhere to verified human resource governance, ensuring fair candidate experiences and statutory compliance.
            </CardDescription>
          </Card>
          <Card style={{ padding: '1.75rem' }}>
            <CardTitle style={{ fontSize: '1.15rem' }}>Focus on Founder ROI</CardTitle>
            <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>
              We eliminate administrative friction and repetitive screening, freeing your leadership team to concentrate on product, clients, and revenue.
            </CardDescription>
          </Card>
        </div>
      </Section>

      {/* Final CTA */}
      <section style={{ padding: '4.5rem 0', backgroundColor: 'var(--color-navy-dark)', color: '#ffffff' }}>
        <Container size="md">
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#ffffff' }}>
              Let's Discuss Your Business Requirements
            </h2>
            <p style={{ fontSize: '1.05rem', color: '#cbd5e1', marginTop: '0.85rem', lineHeight: 1.6 }}>
              Schedule a preliminary consultation to evaluate your recruitment, operational, or marketing support needs.
            </p>
            <div style={{ marginTop: '2rem' }}>
              <Button
                variant="teal"
                size="lg"
                onClick={() => handleConsultation('business_bottom_cta')}
              >
                Book a Consultation →
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};


